import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Stack,
} from '@mui/material';
import BPMNHomePagePreview from "image/LandingPageBPMNPreview.png";
import { getTranslation } from 'common';
import { useTranslations } from 'hooks';

interface Step {
    number: number;
    title: string;
    description: string;
}

const LandingPageBPMNPreview = ({ lang }: { lang?: string }) => {
    const translations = useTranslations()
    useEffect(() => {
        if (lang) {
            // console.log("LandingPageBPMNPreview lang:", lang);
        }
    }, [lang]);

    const steps: Step[] = [
        {
            number: 1,
            title: getTranslation(translations, "step_title.configure_organization"),
            description: getTranslation(translations, "step_description.configure_organization"),
        },
        {
            number: 2,
            title: getTranslation(translations, "step_title.design_and_publish"),
            description: getTranslation(translations, "step_description.design_and_publish"),
        },
        {
            number: 3,
            title: getTranslation(translations, "step_title.start_and_collaborate"),
            description: getTranslation(translations, "step_description.start_and_collaborate")
        },
        {
            number: 4,
            title: getTranslation(translations, "step_title.monitor_and_analyze"),
            description: getTranslation(translations, "step_description.monitor_and_analyze"),
        },
    ];

    return (
        <Box
            sx={{
                // minHeight: '100vh',
                background: 'linear-gradient(135deg, #1a2332 0%, #0f1419 100%)',
                py: 8,
            }}
            id="how-it-works"
        >
            <Container maxWidth="xl">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="center">
                    {/* Left Section - Steps */}
                    <Box sx={{ flex: 1, pl: 8, py: 6 }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                mb: 6,
                                fontSize: { xs: '2rem', md: '2.5rem' },
                            }}
                        >
                            {getTranslation(translations, "how_it_works.title")}
                        </Typography>

                        <Stack spacing={4}>
                            {steps.map((step, index) => (
                                <Box
                                    key={step.number}
                                    sx={{
                                        display: 'flex',
                                        gap: 3,
                                        position: 'relative',
                                        py: 6
                                    }}
                                >
                                    {/* Connecting Line */}
                                    {index < steps.length - 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: '19px',
                                                top: '48px',
                                                width: '2px',
                                                height: 'calc(100% + 16px)',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                            }}
                                        />
                                    )}

                                    {/* Step Number Circle */}
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            flexShrink: 0,
                                            zIndex: 1,
                                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                background: 'white',
                                                color: '#2563eb',
                                                boxShadow: '0 6px 16px rgba(255, 255, 255, 0.3)',
                                            },
                                        }}
                                    >
                                        {step.number}
                                    </Box>

                                    {/* Step Content */}
                                    <Box sx={{ flex: 1, pt: 0.5 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'white',
                                                fontWeight: 600,
                                                mb: 1,
                                                fontSize: '1.25rem',
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    {/* Right Section - BPMN Diagram Preview */}
                    <Box sx={{ flex: 1, maxWidth: '600px', pr: 8 }}>
                        <img
                            src={BPMNHomePagePreview}
                            alt="BPMN Diagram Preview"
                            style={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                            }}
                        />
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default LandingPageBPMNPreview;